import axios from '@/plugins/axios'
import { Message } from 'element-ui'

export default {
  namespaced: true,

  state: {
    videoFiles: [],
    audioFile: null,
    hasVocals: false,
    selectedTier: null, // 1, 2, or 3
    analysisStatus: null, // null, 'uploading', 'analyzing', 'completed', 'error'
    jobId: null,
    errorMessage: '',
    pollingInterval: null,
    analysisResults: null
  },

  mutations: {
    setVideoFiles(state, files) {
      state.videoFiles = files
    },

    setAudioFile(state, file) {
      state.audioFile = file
    },

    setHasVocals(state, value) {
      state.hasVocals = value
    },

    setSelectedTier(state, tier) {
      state.selectedTier = tier
    },

    setAnalysisStatus(state, status) {
      state.analysisStatus = status
    },

    setJobId(state, jobId) {
      state.jobId = jobId
    },

    setErrorMessage(state, message) {
      state.errorMessage = message
    },

    setPollingInterval(state, interval) {
      state.pollingInterval = interval
    },

    setAnalysisResults(state, results) {
      state.analysisResults = results
    },

    resetState(state) {
      state.videoFiles = []
      state.audioFile = null
      state.hasVocals = false
      state.selectedTier = null
      state.analysisStatus = null
      state.jobId = null
      state.errorMessage = ''
      state.analysisResults = null

      if (state.pollingInterval) {
        clearInterval(state.pollingInterval)
        state.pollingInterval = null
      }
    }
  },

  getters: {
    canSubmit(state) {
      return state.videoFiles.length > 0 && state.audioFile !== null && state.selectedTier !== null
    },

    buttonText(state) {
      if (state.analysisStatus === 'uploading') return 'Uploading...'
      if (state.analysisStatus === 'analyzing') return 'Analyzing...'
      return 'Start Analysis'
    }
  },

  actions: {
    /**
     * Start the analysis process
     * @param {Object} params - The parameters for the analysis
     * @param {Object} params.commit - The commit function
     * @param {Object} params.state - The state of the analysis
     * @param {Object} params.dispatch - The dispatch function
     */
    async startAnalysis({ commit, state, dispatch }) {
      if (state.videoFiles.length === 0 || !state.audioFile) {
        Message({
          type: 'warning',
          message: 'Please upload at least one video and one audio file'
        })
        return
      }

      try {
        commit('setAnalysisStatus', 'uploading')
        commit('setErrorMessage', '')

        const formData = new FormData()

        state.videoFiles.forEach((file) => {
          formData.append('videos[]', file.raw)
        })

        formData.append('audio', state.audioFile.raw)
        formData.append('vocals', state.hasVocals)
        formData.append('tier', state.selectedTier)

        const response = await axios.admin.post('ml/video-analysis', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 minutes timeout for upload
        })

        if (response.data && response.data.jobId) {
          commit('setJobId', response.data.jobId)
          commit('setAnalysisStatus', 'analyzing')
          dispatch('startPolling')

          Message({
            type: 'success',
            message: 'Files uploaded successfully. Starting analysis...'
          })
        } else {
          throw new Error('No job ID received from server')
        }
      } catch (error) {
        commit('setAnalysisStatus', 'error')
        const errorMsg = (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to start analysis'
        commit('setErrorMessage', errorMsg)

        Message({ type: 'error', message: errorMsg })
      }
    },

    /**
     * Start polling the analysis status
     * @param {Object} params - The parameters for the analysis
     * @param {Object} params.commit - The commit function
     * @param {Object} params.dispatch - The dispatch function
     */
    startPolling({ commit, dispatch }) {
      const interval = setInterval(() => { dispatch('checkAnalysisStatus') }, 5000)

      commit('setPollingInterval', interval)
    },

    /**
     * Check the analysis status
     * @param {Object} params - The parameters for the analysis
     * @param {Object} params.commit - The commit function
     * @param {Object} params.state - The state of the analysis
     * @param {Object} params.dispatch - The dispatch function
     */
    async checkAnalysisStatus({ commit, state, dispatch }) {
      try {
        const response = await axios.admin.get('ml/video-analysis/' + state.jobId)

        if (response.data && response.data.success === true) {
          commit('setAnalysisStatus', 'completed')

          // Save the analysis results if available
          if (response.data.result) {
            commit('setAnalysisResults', response.data.result)
          }

          dispatch('stopPolling')

          Message({
            type: 'success',
            message: 'Analysis completed successfully!'
          })
        } else if (response.data && response.data.success === false && response.data.error) {
          // If there's an explicit error in the response
          commit('setAnalysisStatus', 'error')
          commit('setErrorMessage', response.data.error || 'Analysis failed')
          dispatch('stopPolling')

          Message({
            type: 'error',
            message: response.data.error || 'Analysis failed'
          })
        }
        // If success is not true, keep polling (still processing)
      } catch (error) {
        console.error('Status check error:', error)
        commit('setAnalysisStatus', 'error')
        const errorMsg = (error.response && error.response.data && error.response.data.message) || 'Failed to check analysis status'
        commit('setErrorMessage', errorMsg)
        dispatch('stopPolling')

        Message({
          type: 'error',
          message: errorMsg
        })
      }
    },

    stopPolling({ commit, state }) {
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval)
        commit('setPollingInterval', null)
      }
    },

    resetForm({ commit }) {
      commit('resetState')
    }
  }
}
